import { useEffect } from "react";

/**
 * Keeps a --scrollbar-gutter CSS var on <html> in sync with the page's actual
 * current scrollbar width (0 if the page isn't scrollable right now). CSS
 * uses it to compensate body's margin-right when react-modal locks scroll
 * (body.modal-open), so the page no longer shifts by a hardcoded guess that
 * didn't match the real scrollbar width (or was applied when there was no
 * scrollbar to begin with).
 *
 * Measurement is skipped while a modal is open: at that point body already
 * has overflow:hidden, so the "current" gutter would read as 0 and blow away
 * the value CSS needs for compensation while it's still locked.
 *
 * Polls instead of relying solely on ResizeObserver/resize: a scrollbar
 * appearing or disappearing purely because content height crossed the
 * viewport threshold (e.g. more rows loaded async) changes clientWidth
 * without the window itself resizing, and documentElement's content-box
 * doesn't reliably fire ResizeObserver for that in every browser. The poll
 * is just a cheap subtraction + comparison, so a short interval is fine.
 */
export default function useScrollbarGutter() {
    useEffect(() => {
        const root = document.documentElement;

        const update = () => {
            if (document.body.classList.contains("modal-open")) return;
            const gutter = window.innerWidth - root.clientWidth;
            root.style.setProperty("--scrollbar-gutter", `${gutter}px`);
        };

        update();
        window.addEventListener("resize", update);
        const intervalId = setInterval(update, 500);

        return () => {
            window.removeEventListener("resize", update);
            clearInterval(intervalId);
        };
    }, []);
}
